/* eslint-disable */
import { useState } from "react";
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { usd, pct, generateId, downloadCSV, C, styles } from "./tokens";

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <span style={{ fontSize: 20, fontWeight: 800, color: C.text, letterSpacing: "-0.02em" }}>{title}</span>
      </div>
      {subtitle && <div style={{ fontSize: 13, color: C.textSec, marginLeft: 32, marginTop: 4 }}>{subtitle}</div>}
    </div>
  );
}

function StatCard({ label, value, sublabel, accent }) {
  const bg = accent
    ? { background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyLight} 100%)`, border: "none" }
    : { background: C.surface, border: `1px solid ${C.borderLight}` };
  return (
    <div style={{ ...bg, borderRadius: 12, padding: "16px 20px", flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: accent ? "#93c5fd" : C.textSec, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, ...styles.mono, color: accent ? "#fff" : C.text }}>{value}</div>
      {sublabel && <div style={{ fontSize: 11, color: accent ? "rgba(255,255,255,.7)" : C.textMuted, marginTop: 4 }}>{sublabel}</div>}
    </div>
  );
}

function HeroBanner({ label, value, subtitle, color = "navy" }) {
  const gradients = {
    navy: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyLight} 100%)`,
    green: `linear-gradient(135deg, ${C.greenDark} 0%, #047857 50%, ${C.green} 100%)`,
    indigo: "linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)",
  };
  return (
    <div style={{ background: gradients[color], borderRadius: 16, padding: "24px 28px", color: "#fff", marginBottom: 20 }}>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, opacity: 0.85 }}>{label}</div>
      <div style={{ fontSize: 42, fontWeight: 900, letterSpacing: "-0.03em", ...styles.mono }}>{value}</div>
      {subtitle && <div style={{ fontSize: 13, opacity: 0.8, marginTop: 6 }}>{subtitle}</div>}
    </div>
  );
}

function MetricLine({ label, value, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${C.surface}` }}>
      <span style={{ fontSize: 13, color: C.textSec }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 700, ...styles.mono, color: color || C.text }}>{value}</span>
    </div>
  );
}

function PillTabs({ tabs, active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, background: "#fff", borderRadius: 12, padding: 4, border: `1px solid ${C.borderLight}`, marginBottom: 20, width: "fit-content" }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          background: active === t.id ? C.navy : "transparent",
          color: active === t.id ? "#fff" : C.textSec,
          border: "none", borderRadius: 8, padding: "7px 16px",
          fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
          transition: "all .15s",
        }}>{t.label}</button>
      ))}
    </div>
  );
}

function SliderField({ label, value, onChange, min, max, step = 0.001, fmt = pct }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={styles.label}>{label}</span>
        <span style={{ fontSize: 18, fontWeight: 800, ...styles.mono, color: C.text }}>{fmt(value)}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: "100%", accentColor: C.navy }} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
        <span style={{ fontSize: 10, color: C.textMuted }}>{fmt(min)}</span>
        <span style={{ fontSize: 10, color: C.textMuted }}>{fmt(max)}</span>
      </div>
    </div>
  );
}

function Callout({ type = "blue", children }) {
  const colors = {
    blue: { bg: "#eff6ff", border: "#bfdbfe" },
    amber: { bg: "#fffbeb", border: "#fde68a" },
  };
  return (
    <div style={{ background: colors[type].bg, border: `1px solid ${colors[type].border}`, borderRadius: 10, padding: 16, marginBottom: 16, fontSize: 13 }}>
      {children}
    </div>
  );
}

// ─── PROJECTION TABLE ─────────────────────────────────────────────────────────
function ProjectionTable({ rows, columns, filename }) {
  if (!rows || !rows.length) return <div style={{ color: C.textMuted, fontSize: 13 }}>No data.</div>;
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button style={styles.btnSecondary} onClick={() => downloadCSV(rows, filename)}>⬇ Download CSV</button>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: C.surface }}>
              {columns.map(c => (
                <th key={c.key} style={{ padding: "8px 12px", textAlign: c.align || "right", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: C.textSec, borderBottom: `1px solid ${C.borderLight}`, whiteSpace: "nowrap" }}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : C.surface }}>
                {columns.map(c => (
                  <td key={c.key} style={{ padding: "7px 12px", textAlign: c.align || "right", ...styles.mono, color: c.color ? c.color(row[c.key]) : C.text, borderBottom: `1px solid ${C.borderLight}` }}>
                    {c.format ? c.format(row[c.key]) : row[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── DUAL AXIS CHART ──────────────────────────────────────────────────────────
function DualAxisChart({ data, lines }) {
  if (!data || !data.length) return null;
  const fmtK = (v) => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${v}`;
  return (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart data={data} margin={{ top: 10, right: 30, bottom: 10, left: 10 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={C.borderLight} />
        <XAxis dataKey="Year" tick={{ fontSize: 11, fontFamily: "Inter, sans-serif", fill: C.textSec }} />
        <YAxis yAxisId="left" tickFormatter={fmtK} tick={{ fontSize: 11, fontFamily: "Inter, sans-serif", fill: C.textSec }} />
        <YAxis yAxisId="right" orientation="right" tickFormatter={fmtK} tick={{ fontSize: 11, fontFamily: "Inter, sans-serif", fill: C.textSec }} />
        <Tooltip formatter={(v, n) => [usd(v), n]} contentStyle={{ fontSize: 12, fontFamily: "DM Mono, monospace" }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {lines.map(l => (
          <Line key={l.key} yAxisId={l.axis} type="monotone" dataKey={l.key} name={l.name}
            stroke={l.color} strokeWidth={2} dot={{ r: 3 }} strokeDasharray={l.dashed ? "5 3" : undefined} />
        ))}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

// ─── FORM LAYOUT HELPERS (module-level — must NOT be defined inside components) ─
function FormGrid({ children, cols = 2 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16, marginBottom: 16 }}>
      {children}
    </div>
  );
}
function FormField({ label, children }) {
  return <div><label style={styles.label}>{label}</label>{children}</div>;
}

// ─── ACCOUNT FORM ─────────────────────────────────────────────────────────────
function AccountForm({ onAdd, onCancel }) {
  const [form, setForm] = useState({
    account_number: "", balance: "", is_annuity: false, is_inherited_ira: false,
    fair_market_value: "", annual_income: "", annuity_start_date: "", annuity_end_date: "",
    descendent_dob: "", dob_selection: 0, date_of_death: "", beneficiary_type: "edb", annuity_type: "income",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleAdd = () => {
    if (!form.account_number) return;
    onAdd({
      id: generateId(),
      account_number: form.account_number,
      balance: parseFloat(form.balance) || 0,
      is_annuity: form.is_annuity,
      is_inherited_ira: form.is_inherited_ira,
      ...(form.is_annuity ? {
        annuity_type: form.annuity_type,
        fair_market_value: parseFloat(form.fair_market_value) || 0,
        annual_income: parseFloat(form.annual_income) || 0,
        annuity_start_date: form.annuity_start_date,
        annuity_end_date: form.annuity_end_date,
      } : {}),
      ...(form.is_inherited_ira ? {
        descendent_dob: form.descendent_dob,
        dob_selection: Number(form.dob_selection),
        date_of_death: form.date_of_death,
        beneficiary_type: form.beneficiary_type,
      } : {}),
    });
  };

  return (
    <div style={{ background: "#f0f4fa", borderRadius: 12, padding: 20, marginTop: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 16 }}>Add Account</div>
      <FormGrid>
        <FormField label="Account #">
          <input style={styles.input} value={form.account_number} onChange={e => set("account_number", e.target.value)} placeholder="e.g. 1234" />
        </FormField>
        <FormField label="Balance ($)">
          <input style={styles.input} type="number" value={form.balance} onChange={e => set("balance", e.target.value)} placeholder="0.00" />
        </FormField>
      </FormGrid>
      <FormGrid>
        <FormField label="Is Annuity?">
          <select style={styles.input} value={form.is_annuity ? "yes" : "no"} onChange={e => set("is_annuity", e.target.value === "yes")}>
            <option value="no">No</option><option value="yes">Yes (Annuity)</option>
          </select>
        </FormField>
        <FormField label="Is Inherited IRA?">
          <select style={styles.input} value={form.is_inherited_ira ? "yes" : "no"} onChange={e => set("is_inherited_ira", e.target.value === "yes")}>
            <option value="no">No</option><option value="yes">Yes (Inherited)</option>
          </select>
        </FormField>
      </FormGrid>
      {form.is_annuity && (
        <FormGrid cols={2}>
          <FormField label="Annuity Type">
            <select style={styles.input} value={form.annuity_type} onChange={e => set("annuity_type", e.target.value)}>
              <option value="income">Income Annuity (SPIA / period certain)</option>
              <option value="fia">Fixed Index Annuity (FIA)</option>
              <option value="deferred">Other Deferred Annuity</option>
            </select>
          </FormField>
          <FormField label="Fair Market Value ($)">
            <input style={styles.input} type="number" value={form.fair_market_value} onChange={e => set("fair_market_value", e.target.value)} />
          </FormField>
          <FormField label="Annual Income ($)">
            <input style={styles.input} type="number" value={form.annual_income} onChange={e => set("annual_income", e.target.value)} />
          </FormField>
          <FormField label="Annuity Start Date">
            <input style={styles.input} type="date" value={form.annuity_start_date} onChange={e => set("annuity_start_date", e.target.value)} />
          </FormField>
          <FormField label="Annuity End Date">
            <input style={styles.input} type="date" value={form.annuity_end_date} onChange={e => set("annuity_end_date", e.target.value)} />
          </FormField>
        </FormGrid>
      )}
      {form.is_inherited_ira && (
        <FormGrid>
          <FormField label="Decedent DOB">
            <input style={styles.input} type="date" value={form.descendent_dob} onChange={e => set("descendent_dob", e.target.value)} />
          </FormField>
          <FormField label="Date of Death">
            <input style={styles.input} type="date" value={form.date_of_death} onChange={e => set("date_of_death", e.target.value)} />
          </FormField>
          <FormField label="Beneficiary Type">
            <select style={styles.input} value={form.beneficiary_type} onChange={e => set("beneficiary_type", e.target.value)}>
              <option value="edb">Eligible designated (stretch)</option>
              <option value="dcb">Designated, non-eligible (10-year rule)</option>
              <option value="non">Non-designated (5-year / ghost life)</option>
            </select>
          </FormField>
          <FormField label="Life Expectancy Anchor">
            <select style={styles.input} value={form.dob_selection} onChange={e => set("dob_selection", Number(e.target.value))}>
              <option value={0}>Beneficiary (this person's DOB)</option>
              <option value={1}>Decedent DOB (ghost life)</option>
            </select>
          </FormField>
        </FormGrid>
      )}
      <div style={{ display: "flex", gap: 10 }}>
        <button style={styles.btnPrimary} onClick={handleAdd}>Add Account</button>
        <button style={styles.btnSecondary} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ─── ACCOUNT EDIT FORM ────────────────────────────────────────────────────────
function AccountEditForm({ account, onSave, onCancel }) {
  const [form, setForm] = useState({
    account_number: account.account_number || "",
    balance: account.balance ?? "",
    is_annuity: account.is_annuity || false,
    is_inherited_ira: account.is_inherited_ira || false,
    fair_market_value: account.fair_market_value ?? "",
    annual_income: account.annual_income ?? "",
    annuity_start_date: account.annuity_start_date || "",
    annuity_end_date: account.annuity_end_date || "",
    descendent_dob: account.descendent_dob || "",
    dob_selection: account.dob_selection ?? 0,
    date_of_death: account.date_of_death || "",
    beneficiary_type: account.beneficiary_type || "edb",
    annuity_type: account.annuity_type || "income",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = () => {
    if (!form.account_number) return;
    onSave({
      ...account,
      account_number: form.account_number,
      balance: parseFloat(form.balance) || 0,
      is_annuity: form.is_annuity,
      is_inherited_ira: form.is_inherited_ira,
      ...(form.is_annuity ? {
        annuity_type: form.annuity_type,
        fair_market_value: parseFloat(form.fair_market_value) || 0,
        annual_income: parseFloat(form.annual_income) || 0,
        annuity_start_date: form.annuity_start_date,
        annuity_end_date: form.annuity_end_date,
      } : { annuity_type: undefined, fair_market_value: undefined, annual_income: undefined, annuity_start_date: undefined, annuity_end_date: undefined }),
      ...(form.is_inherited_ira ? {
        descendent_dob: form.descendent_dob,
        dob_selection: Number(form.dob_selection),
        date_of_death: form.date_of_death,
        beneficiary_type: form.beneficiary_type,
      } : { descendent_dob: undefined, dob_selection: undefined, date_of_death: undefined, beneficiary_type: undefined }),
    });
  };

  return (
    <div style={{ background: "#fffbeb", border: `1px solid #fcd34d`, borderRadius: 12, padding: 20, marginTop: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 16 }}>
        ✏️ Edit Account — <span style={{ ...styles.mono, color: C.textSec }}>{account.account_number}</span>
      </div>
      <FormGrid>
        <FormField label="Account #">
          <input style={styles.input} value={form.account_number} onChange={e => set("account_number", e.target.value)} />
        </FormField>
        <FormField label="Balance ($)">
          <input style={styles.input} type="number" value={form.balance} onChange={e => set("balance", e.target.value)} />
        </FormField>
      </FormGrid>
      <FormGrid>
        <FormField label="Is Annuity?">
          <select style={styles.input} value={form.is_annuity ? "yes" : "no"} onChange={e => set("is_annuity", e.target.value === "yes")}>
            <option value="no">No</option><option value="yes">Yes (Annuity)</option>
          </select>
        </FormField>
        <FormField label="Is Inherited IRA?">
          <select style={styles.input} value={form.is_inherited_ira ? "yes" : "no"} onChange={e => set("is_inherited_ira", e.target.value === "yes")}>
            <option value="no">No</option><option value="yes">Yes (Inherited)</option>
          </select>
        </FormField>
      </FormGrid>
      {form.is_annuity && (
        <FormGrid cols={2}>
          <FormField label="Annuity Type">
            <select style={styles.input} value={form.annuity_type} onChange={e => set("annuity_type", e.target.value)}>
              <option value="income">Income Annuity (SPIA / period certain)</option>
              <option value="fia">Fixed Index Annuity (FIA)</option>
              <option value="deferred">Other Deferred Annuity</option>
            </select>
          </FormField>
          <FormField label="Fair Market Value ($)">
            <input style={styles.input} type="number" value={form.fair_market_value} onChange={e => set("fair_market_value", e.target.value)} />
          </FormField>
          <FormField label="Annual Income ($)">
            <input style={styles.input} type="number" value={form.annual_income} onChange={e => set("annual_income", e.target.value)} />
          </FormField>
          <FormField label="Annuity Start Date">
            <input style={styles.input} type="date" value={form.annuity_start_date} onChange={e => set("annuity_start_date", e.target.value)} />
          </FormField>
          <FormField label="Annuity End Date">
            <input style={styles.input} type="date" value={form.annuity_end_date} onChange={e => set("annuity_end_date", e.target.value)} />
          </FormField>
        </FormGrid>
      )}
      {form.is_inherited_ira && (
        <FormGrid>
          <FormField label="Decedent DOB">
            <input style={styles.input} type="date" value={form.descendent_dob} onChange={e => set("descendent_dob", e.target.value)} />
          </FormField>
          <FormField label="Date of Death">
            <input style={styles.input} type="date" value={form.date_of_death} onChange={e => set("date_of_death", e.target.value)} />
          </FormField>
          <FormField label="Beneficiary Type">
            <select style={styles.input} value={form.beneficiary_type} onChange={e => set("beneficiary_type", e.target.value)}>
              <option value="edb">Eligible designated (stretch)</option>
              <option value="dcb">Designated, non-eligible (10-year rule)</option>
              <option value="non">Non-designated (5-year / ghost life)</option>
            </select>
          </FormField>
          <FormField label="Life Expectancy Anchor">
            <select style={styles.input} value={form.dob_selection} onChange={e => set("dob_selection", Number(e.target.value))}>
              <option value={0}>Beneficiary (this person's DOB)</option>
              <option value={1}>Decedent DOB (ghost life)</option>
            </select>
          </FormField>
        </FormGrid>
      )}
      <div style={{ display: "flex", gap: 10 }}>
        <button style={styles.btnPrimary} onClick={handleSave}>Save Changes</button>
        <button style={styles.btnSecondary} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}


export { SectionHeader, StatCard, HeroBanner, MetricLine, PillTabs, SliderField, Callout, ProjectionTable, DualAxisChart, FormGrid, FormField, AccountForm, AccountEditForm };
